import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const resetOptions = [
  ['NEVER', 'Nunca'],
  ['DAILY', 'Diário'],
  ['MONTHLY', 'Mensal'],
  ['YEARLY', 'Anual'],
];

const blank = () => ({
  name: '',
  mask: '{SEQ:5}',
  currentValue: 0,
  increment: 1,
  resetMode: 'NEVER',
});

function formatMask(mask, value) {
  return (mask || '').replace(/\{SEQ(?::(\d+))?\}/g, (_m, width) => String(value).padStart(width ? Number(width) : 0, '0'));
}

export default function Counters({ user }) {
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
  const [list, setList] = useState([]);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  async function load() {
    try {
      setList(await api.listCounters());
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function create(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    try {
      await api.createCounter(form);
      setForm(blank());
      setStatus('Contador criado.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function increment(counter) {
    try {
      await api.incrementCounter(counter.id);
      setStatus(`Contador "${counter.name}" avançado.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(counter) {
    if (!confirm(`Remover o contador "${counter.name}"?`)) return;
    try {
      await api.deleteCounter(counter.id);
      setStatus('Contador removido.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="flex flex-col gap-base">
      <div>
        <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Gestão de Contadores</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-2">
          Sequências numéricas com regras de incremento e reset para lotes, séries e pallets.
        </p>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}
      {status && <p className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</p>}

      {canManage && (
        <form onSubmit={create} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Novo contador</h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Nome</span>
              <input value={form.name} onChange={(e) => update('name', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Máscara</span>
              <input value={form.mask} onChange={(e) => update('mask', e.target.value)} placeholder="LOTE-{YYYY}{SEQ:3}" className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 font-mono text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Valor atual</span>
              <input type="number" min="0" value={form.currentValue} onChange={(e) => update('currentValue', Number(e.target.value))} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Incremento</span>
              <input type="number" min="1" value={form.increment} onChange={(e) => update('increment', Number(e.target.value))} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Reset</span>
              <select value={form.resetMode} onChange={(e) => update('resetMode', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15">
                {resetOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <button type="submit" className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-6 py-3 rounded-lg transition-all shadow-md shadow-primary/20">
              Criar contador
            </button>
          </div>
        </form>
      )}

      <div className="bg-surface rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-container-lowest text-on-surface-variant font-label-sm text-label-sm uppercase tracking-wider">
              <th className="px-6 py-4 font-semibold">Contador</th>
              <th className="px-6 py-4 font-semibold">Máscara</th>
              <th className="px-6 py-4 font-semibold">Atual</th>
              <th className="px-6 py-4 font-semibold">Incremento</th>
              <th className="px-6 py-4 font-semibold">Reset</th>
              <th className="px-6 py-4 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-container-highest font-body-md text-body-md text-on-surface">
            {list.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-on-surface-variant">Nenhum contador cadastrado.</td>
              </tr>
            ) : (
              list.map((counter) => (
                <tr key={counter.id} className="hover:bg-surface-container-low transition-colors">
                  <td className="px-6 py-4 font-semibold">{counter.name}</td>
                  <td className="px-6 py-4">
                    <code className="bg-surface-container-low rounded-lg px-3 py-1.5 text-[11px] text-on-primary-container">{formatMask(counter.mask, counter.currentValue)}</code>
                  </td>
                  <td className="px-6 py-4">{counter.currentValue.toLocaleString('pt-BR')}</td>
                  <td className="px-6 py-4">{counter.increment}</td>
                  <td className="px-6 py-4 text-on-surface-variant">{resetOptions.find(([v]) => v === counter.resetMode)?.[1] || counter.resetMode}</td>
                  <td className="px-6 py-4">
                    {canManage && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => increment(counter)} title="Avançar contador" className="rounded-lg border border-surface-container-high px-3 py-1.5 text-[10px] font-medium text-on-surface">
                          Avançar
                        </button>
                        <button onClick={() => remove(counter)} className="rounded-lg border border-error/20 bg-error-container/50 px-3 py-1.5 text-[10px] font-medium text-error">
                          Excluir
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
